import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { io, Socket } from "socket.io-client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import { buildServiceDeepLink } from "../components/services/hooks/useServiceDeepLink";
import { getCategoryName } from "../components/services/utils/getCategoryName";
import { useNotifications } from "./NotificationsContext";

/**
 * Show a native browser notification when tab is not active
 * Falls back to toast if notifications not supported/permitted
 */
function showNativeNotification(
  title: string,
  options: {
    body: string;
    icon?: string;
    tag?: string;
    url?: string;
  }
): boolean {
  // Only show native notification if tab is hidden and notifications are permitted
  if (
    !document.hidden ||
    !("Notification" in window) ||
    Notification.permission !== "granted"
  ) {
    return false;
  }

  try {
    const notification = new Notification(title, {
      body: options.body,
      icon: options.icon || "/logo.png",
      tag: options.tag,
      requireInteraction: false,
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
      if (options.url) {
        window.location.href = options.url;
      }
    };

    // Auto-close after 10 seconds
    setTimeout(() => notification.close(), 10000);
    
    return true;
  } catch (error) {
    console.error("[Socket] Native notification failed:", error);
    return false;
  }
}

// Socket event types matching backend
interface NewOrderNotification {
  notificationId?: string;
  projectId?: string;
  orderId: string;
  serviceType: string;
  serviceName: string;
  serviceIcon?: string;
  title: string;
  description: string;
  createdAt: string;
}

interface NewProposalNotification {
  notificationId?: string;
  projectId?: string;
  orderId: string;
  proposalId: string;
  serviceType?: string;
  serviceName?: string;
  serviceIcon?: string;
  providerName: string;
  price: number;
  timeline: string;
  message?: string;
  createdAt: string;
}

interface ProposalStatusNotification {
  notificationId?: string;
  projectId?: string;
  orderId: string;
  proposalId: string;
  serviceType?: string;
  serviceName?: string;
  serviceIcon?: string;
  status: "ACCEPTED" | "REJECTED";
  orderTitle: string;
}

interface ContractNotification {
  notificationId?: string;
  projectId?: string;
  contractId: string;
  // Absent for framework (partner) contracts
  orderId?: string;
  orderTitle?: string;
  serviceType?: string;
  serviceName?: string;
  serviceIcon?: string;
  status: string;
  signedBy?: string;
}

// Outbound service request whose recipient is an in-system provider — the
// e-mail is the delivery channel, this is the courtesy in-app signal.
interface PartnerRequestNotification {
  notificationId?: string;
  projectId?: string;
  requestId: string;
  buildingName: string;
  categoryKey: string;
}

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
});

const API_URL = process.env.REACT_APP_API_URL || "";

/**
 * How long to wait before re-connecting after a *server-initiated* disconnect.
 * Matches socket.io's own `reconnectionDelayMax`, so a fail-closed sweep during
 * an outage produces the same reconnect cadence as a transport drop rather than
 * a tighter loop (ticket 12).
 */
const SERVER_DISCONNECT_RECONNECT_MS = 5000;
/** Trailing window that folds a burst of events into a single notifications GET. */
const NOTIFICATIONS_REFRESH_COALESCE_MS = 400;

export function SocketProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, entityType, entityId } = useAuth();
  const navigate = useNavigate();
  const { refresh } = useNotifications();
  const { t, i18n } = useTranslation();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const tRef = useRef(t);
  tRef.current = t;
  const languageRef = useRef(i18n.language);
  languageRef.current = i18n.language;
  /** The category of an incoming event, in the language on screen (BUG-1). */
  const serviceLabel = (event: { serviceType?: string; serviceName?: string }) =>
    getCategoryName(
      { key: event.serviceType, name: event.serviceName },
      languageRef.current,
      tRef.current
    );
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  /**
   * One GET per burst, not one per event. Awarding an order emits to the
   * winner and to every rejected bidder, and a signature run emits twice in a
   * row; before BUG-1 those were absorbed by the optimistic local write, and
   * refetching per event would put that burst straight on the wire. The edge
   * is trailing: the notification we are reacting to has to be in the response.
   */
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleNotificationsRefresh = useCallback(() => {
    if (refreshTimerRef.current) return;
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      void refreshRef.current?.();
    }, NOTIFICATIONS_REFRESH_COALESCE_MS);
  }, []);
  useEffect(
    () => () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    },
    []
  );

  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Use ref to track the current socket for reliable cleanup
  const socketRef = useRef<Socket | null>(null);
  // Track if we're currently connecting to prevent duplicate connections
  const isConnectingRef = useRef(false);
  // Track the current entity to detect changes
  const currentEntityRef = useRef<string | null>(null);
  // Pending manual reconnect after a server-initiated disconnect (ticket 12)
  const serverDisconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Memoized event handlers to prevent recreation on every render
  const handleNewOrder = useCallback(
    (data: NewOrderNotification) => {
      const deepLinkUrl = buildServiceDeepLink({
        tab: "available",
        serviceType: data.serviceType,
        orderId: data.orderId,
      });

      console.log("[Socket] new_order deep link:", deepLinkUrl, data);

      // Try native notification first if tab is hidden
      const nativeShown = showNativeNotification(tRef.current("notifications.toast.newOrder", { service: serviceLabel(data) }), {
        body: data.title,
        tag: `order-${data.orderId}`,
        url: deepLinkUrl,
      });

      // Always show toast (will be visible when user returns to tab)
      if (!nativeShown) {
        toast.info(tRef.current("notifications.toast.newOrder", { service: serviceLabel(data) }), {
          description: data.title,
          action: {
            label: tRef.current("notifications.toast.view"),
            onClick: () => {
              console.log("[Socket] View clicked (new_order):", deepLinkUrl);
              navigateRef.current(deepLinkUrl);
            },
          },
          duration: 10000,
        });
      }

      // The list is the server's: it renders every notification in the reader's
      // language (BUG-1), and an id the server never stored is one the local
      // list would keep for ever under a made-up key.
      scheduleNotificationsRefresh();
    },
    [scheduleNotificationsRefresh]
  );

  const handleNewProposal = useCallback(
    (data: NewProposalNotification) => {
      const deepLinkUrl = buildServiceDeepLink({
        tab: "orders",
        orderId: data.orderId,
        proposalId: data.proposalId,
      });

      console.log("[Socket] new_proposal deep link:", deepLinkUrl, data);

      // Try native notification first if tab is hidden
      const nativeShown = showNativeNotification(tRef.current("notifications.toast.newProposal", { actor: data.providerName ?? "" }), {
        body: `€${data.price} • ${data.timeline}`,
        tag: `proposal-${data.proposalId}`,
        url: deepLinkUrl,
      });

      if (!nativeShown) {
        toast.info(tRef.current("notifications.toast.newProposal", { actor: data.providerName ?? "" }), {
          description: `€${data.price} • ${data.timeline}`,
          action: {
            label: tRef.current("notifications.toast.view"),
            onClick: () => {
              console.log("[Socket] View clicked (new_proposal):", deepLinkUrl);
              navigateRef.current(deepLinkUrl);
            },
          },
          duration: 10000,
        });
      }

      // The list is the server's: it renders every notification in the reader's
      // language (BUG-1), and an id the server never stored is one the local
      // list would keep for ever under a made-up key.
      scheduleNotificationsRefresh();
    },
    [scheduleNotificationsRefresh]
  );

  const handleProposalStatus = useCallback(
    (data: ProposalStatusNotification) => {
      // For ACCEPTED proposals, navigate to "active" tab; for REJECTED - to "applied"
      const targetTab = data.status === "ACCEPTED" ? "active" : "applied";
      const deepLinkUrl = buildServiceDeepLink({
        tab: targetTab,
        orderId: data.orderId,
        proposalId: data.proposalId,
      });

      console.log("[Socket] proposal_status deep link:", deepLinkUrl, data);

      const title = tRef.current(
        data.status === "ACCEPTED"
          ? "notifications.toast.proposalAccepted"
          : "notifications.toast.proposalRejected"
      );
      const description = tRef.current(
        data.status === "ACCEPTED"
          ? "notifications.toast.proposalAcceptedBody"
          : "notifications.toast.proposalRejectedBody",
        { orderTitle: data.orderTitle }
      );

      // Try native notification first if tab is hidden
      const nativeShown = showNativeNotification(title, {
        body: description,
        tag: `proposal-status-${data.proposalId}`,
        url: deepLinkUrl,
      });

      if (!nativeShown) {
        const toastFn = data.status === "ACCEPTED" ? toast.success : toast.error;
        toastFn(title, {
          description,
          action: {
            label: tRef.current("notifications.toast.view"),
            onClick: () => {
              console.log(
                "[Socket] View clicked (proposal_status):",
                deepLinkUrl
              );
              navigateRef.current(deepLinkUrl);
            },
          },
          duration: data.status === "ACCEPTED" ? 15000 : 10000,
        });
      }

      // The list is the server's: it renders every notification in the reader's
      // language (BUG-1), and an id the server never stored is one the local
      // list would keep for ever under a made-up key.
      scheduleNotificationsRefresh();
    },
    [scheduleNotificationsRefresh]
  );

  // Contract event handlers
  const handleContractCreated = useCallback(
    (data: ContractNotification) => {
      // Framework (partner) contracts have no linked order — deep link to the contract itself
      const deepLinkUrl = data.orderId
        ? buildServiceDeepLink({ tab: "active", orderId: data.orderId })
        : `/partners/${data.contractId}`;

      console.log("[Socket] contract_created deep link:", deepLinkUrl, data);

      const description = data.orderTitle
        ? tRef.current("notifications.toast.contractCreatedBody", { orderTitle: data.orderTitle })
        : tRef.current("notifications.toast.contractCreatedBodyPlain");

      // Try native notification first if tab is hidden
      const nativeShown = showNativeNotification(tRef.current("notifications.toast.contractCreated"), {
        body: description,
        tag: `contract-${data.contractId}`,
        url: deepLinkUrl,
      });

      if (!nativeShown) {
        toast.info(tRef.current("notifications.toast.contractCreated"), {
          description,
          action: {
            label: tRef.current("notifications.toast.view"),
            onClick: () => navigateRef.current(deepLinkUrl),
          },
          duration: 10000,
        });
      }

      // The list is the server's: it renders every notification in the reader's
      // language (BUG-1), and an id the server never stored is one the local
      // list would keep for ever under a made-up key.
      scheduleNotificationsRefresh();
    },
    [scheduleNotificationsRefresh]
  );

  const handleContractSigned = useCallback(
    (data: ContractNotification) => {
      const deepLinkUrl = data.orderId
        ? buildServiceDeepLink({ tab: "active", orderId: data.orderId })
        : `/partners/${data.contractId}`;

      console.log("[Socket] contract_signed deep link:", deepLinkUrl, data);

      const signerLabel = tRef.current(
        data.signedBy === "provider"
          ? "notifications.toast.signerProvider"
          : "notifications.toast.signerBuilding"
      );
      const title = tRef.current("notifications.toast.contractSigned", { actor: signerLabel });
      const description = data.orderTitle
        ? tRef.current("notifications.toast.contractSignedBody", { orderTitle: data.orderTitle })
        : tRef.current("notifications.toast.contractSignedBodyPlain");
      
      // Try native notification first if tab is hidden
      const nativeShown = showNativeNotification(title, {
        body: description,
        tag: `contract-signed-${data.contractId}`,
        url: deepLinkUrl,
      });

      if (!nativeShown) {
        toast.info(title, {
          description,
          action: {
            label: tRef.current("notifications.toast.view"),
            onClick: () => navigateRef.current(deepLinkUrl),
          },
          duration: 15000,
        });
      }

      // The list is the server's: it renders every notification in the reader's
      // language (BUG-1), and an id the server never stored is one the local
      // list would keep for ever under a made-up key.
      scheduleNotificationsRefresh();
    },
    [scheduleNotificationsRefresh]
  );

  const handleContractReady = useCallback(
    (data: ContractNotification) => {
      const deepLinkUrl = data.orderId
        ? buildServiceDeepLink({ tab: "active", orderId: data.orderId })
        : `/partners/${data.contractId}`;

      console.log("[Socket] contract_ready deep link:", deepLinkUrl, data);

      const title = tRef.current("notifications.toast.contractFullySigned");
      const description = data.orderTitle
        ? tRef.current("notifications.toast.contractReadyBody", { orderTitle: data.orderTitle })
        : tRef.current("notifications.toast.contractReadyBodyPlain");

      // Try native notification first if tab is hidden
      const nativeShown = showNativeNotification(title, {
        body: description,
        tag: `contract-ready-${data.contractId}`,
        url: deepLinkUrl,
      });

      if (!nativeShown) {
        toast.success(title, {
          description,
          action: {
            label: tRef.current("notifications.toast.view"),
            onClick: () => navigateRef.current(deepLinkUrl),
          },
          duration: 15000,
        });
      }

      // The list is the server's: it renders every notification in the reader's
      // language (BUG-1), and an id the server never stored is one the local
      // list would keep for ever under a made-up key.
      scheduleNotificationsRefresh();
    },
    [scheduleNotificationsRefresh]
  );

  const handlePartnerRequest = useCallback(
    (data: PartnerRequestNotification) => {
      const url = "/partners";
      const title = tRef.current("notifications.toast.serviceRequest");
      const description = tRef.current("notifications.toast.serviceRequestBody", {
        actor: data.buildingName,
      });

      const nativeShown = showNativeNotification(title, {
        body: description,
        tag: `partner-request-${data.requestId}`,
        url,
      });
      if (!nativeShown) {
        toast.info(title, {
          description,
          action: { label: tRef.current("notifications.toast.view"), onClick: () => navigateRef.current(url) },
          duration: 10000,
        });
      }

      // The list is the server's: it renders every notification in the reader's
      // language (BUG-1), and an id the server never stored is one the local
      // list would keep for ever under a made-up key.
      scheduleNotificationsRefresh();
    },
    [scheduleNotificationsRefresh]
  );

  useEffect(() => {
    // Create a unique entity key to track changes
    const entityKey = isAuthenticated ? `${entityType}:${entityId}` : null;

    // If entity hasn't changed and we already have a connection, skip
    if (
      entityKey === currentEntityRef.current &&
      socketRef.current?.connected
    ) {
      return;
    }

    // Cleanup function that properly disconnects the socket
    const cleanup = () => {
      if (serverDisconnectTimerRef.current) {
        clearTimeout(serverDisconnectTimerRef.current);
        serverDisconnectTimerRef.current = null;
      }
      if (socketRef.current) {
        console.log("Cleaning up socket connection:", socketRef.current.id);
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      isConnectingRef.current = false;
    };

    // If not authenticated, just cleanup and return
    if (!isAuthenticated) {
      currentEntityRef.current = null;
      cleanup();
      return;
    }

    // Prevent duplicate connections
    if (isConnectingRef.current) {
      console.log("Already connecting, skipping duplicate connection attempt");
      return;
    }

    // Clean up existing socket before creating new one
    cleanup();

    // Mark that we're connecting
    isConnectingRef.current = true;
    currentEntityRef.current = entityKey;

    console.log("Creating new socket connection for:", entityKey);

    // Create socket connection with reconnection settings
    const newSocket = io(API_URL || window.location.origin, {
      withCredentials: true,
      transports: ["polling", "websocket"],
      reconnection: true,
      // Unbounded since ticket 12: the server now drops sockets fail-closed
      // when it cannot revalidate their session, so a 15-second Redis blip
      // would otherwise exhaust a 5-attempt ceiling and cost real-time updates
      // for the rest of the browser session. Bounded by the delay cap below.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    // Store in ref immediately
    socketRef.current = newSocket;

    newSocket.on("connect", () => {
      console.log("Socket connected:", newSocket.id);
      isConnectingRef.current = false;
      setIsConnected(true);
      setSocket(newSocket);
      // Sockets have no replay, and since ticket 12 a server-initiated
      // disconnect is routine (the fail-closed sweep), so every notification
      // emitted while this tab was down is otherwise lost from the bell until
      // something else refetches. Reconcile on every (re)connect (ticket 16,
      // audit F15). Read through a ref so this cannot re-run the effect that
      // created the socket.
      void refreshRef.current?.();
    });

    newSocket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);
      setIsConnected(false);

      // socket.io-client deliberately does NOT auto-reconnect after
      // `io server disconnect`, and since ticket 12 that is the *normal* reason
      // this socket ends: every `disconnectSockets(true)` on the server — an
      // identity transition, or a fail-closed sweep tick during a Redis blip —
      // produces exactly this reason. Without a manual reconnect,
      // `reconnectionAttempts: Infinity` above buys nothing for the disconnects
      // this ticket introduced: a still-legitimate tab would silently lose all
      // real-time updates until the user reloaded the page, which is precisely
      // the outcome that made fail-closed acceptable in the first place
      // (spec decision 6). Found by ticket 12's smoke test.
      //
      // A session that is genuinely gone simply fails the handshake, and the
      // 401 interceptor is already navigating that tab to /login; a refused
      // handshake is cheap, and socket.io's own backoff (1 s → 5 s) bounds the
      // retries from there.
      if (reason === "io server disconnect" && socketRef.current === newSocket) {
        serverDisconnectTimerRef.current = setTimeout(() => {
          if (socketRef.current === newSocket && !newSocket.connected) {
            console.log("Reconnecting after a server-initiated disconnect");
            newSocket.connect();
          }
        }, SERVER_DISCONNECT_RECONNECT_MS);
      }
    });

    newSocket.on("connect_error", (error) => {
      console.log("Socket connection error:", error.message);
      isConnectingRef.current = false;
      setIsConnected(false);
    });

    // Register event handlers
    newSocket.on("new_order", handleNewOrder);
    newSocket.on("new_proposal", handleNewProposal);
    newSocket.on("proposal_status", handleProposalStatus);
    newSocket.on("contract_created", handleContractCreated);
    newSocket.on("contract_signed", handleContractSigned);
    newSocket.on("contract_ready", handleContractReady);
    newSocket.on("partner_request", handlePartnerRequest);

    // Return cleanup function
    return cleanup;
  }, [
    isAuthenticated,
    entityType,
    entityId,
    handleNewOrder,
    handleNewProposal,
    handleProposalStatus,
    handleContractCreated,
    handleContractSigned,
    handleContractReady,
    handlePartnerRequest,
  ]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}
